import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { api, friendlyError } from '../lib/api-client';
import type { PartnerPortalMe } from '@accestra/types';
import Container from '../components/ui/Container';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import AlertBanner from '../components/ui/AlertBanner';
import SectionHeader from '../components/ui/SectionHeader';
import {
  PartnerHeaderMeta,
  PartnerReferralLinkCard,
} from '../components/partner-portal/shared';
import type { PartnerMeResponse } from '../lib/partner-portal';

const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid var(--acc-color-border)',
  borderRadius: 12,
  padding: '11px 12px',
  background: 'var(--input-bg)',
  color: 'var(--acc-color-text)',
  fontSize: 14,
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--acc-color-text-muted)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

function formFromMe(me: PartnerPortalMe) {
  return {
    name: me.partner.name,
    contactName: me.partner.contactName ?? '',
    email: me.partner.email,
    websiteUrl: me.partner.websiteUrl ?? '',
    linkedinUrl: me.partner.linkedinUrl ?? '',
    payoutNotes: me.partner.payoutNotes ?? '',
  };
}

export default function PartnerSettingsPage() {
  const { t } = useTranslation(['partnerPortal', 'common']);
  const [me, setMe] = useState<PartnerPortalMe | null>(null);
  const [form, setForm] = useState({
    name: '',
    contactName: '',
    email: '',
    websiteUrl: '',
    linkedinUrl: '',
    payoutNotes: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get<PartnerMeResponse>('/partner/me');
        setMe(response.me);
        setForm(formFromMe(response.me));
      } catch (err) {
        setError(friendlyError(err, t('errors.load')));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [t]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setFeedback(null);

    try {
      const response = await api.patch<PartnerMeResponse>('/partner/me', form);
      setMe(response.me);
      setForm(formFromMe(response.me));
      setFeedback(t('settings.saved'));
    } catch (err) {
      setError(friendlyError(err, t('settings.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth={1180}>
      <div style={{ display: 'grid', gap: 20 }}>
        <PageHeader
          eyebrow={t('settings.eyebrow')}
          title={t('settings.title')}
          subtitle={me?.partner.name}
          description={t('settings.description')}
          meta={me ? <PartnerHeaderMeta access={me.access} /> : undefined}
        />

        {feedback && <AlertBanner variant="success">{feedback}</AlertBanner>}
        {error && <AlertBanner variant="danger">{error}</AlertBanner>}

        {loading || !me ? (
          <Card>{t('common:actions.loading')}</Card>
        ) : (
          <>
            <PartnerReferralLinkCard
              label={t('fields.referralLink')}
              title={t('settings.referralTitle')}
              description={t('settings.referralDescription')}
              note={t('settings.referralNote')}
              referralLink={me.referralLink}
              copyLabel={t('actions.copyLink')}
              onCopied={() => setFeedback(t('actions.copied'))}
              onCopyFailed={() => setFeedback(t('actions.copyFailed'))}
            />

            <Card>
              <div style={{ display: 'grid', gap: 18 }}>
                <SectionHeader
                  eyebrow={t('settings.profileEyebrow')}
                  title={t('settings.profileTitle')}
                  subtitle={t('settings.profileDescription')}
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                  <label>
                    <span style={labelStyle}>{t('fields.displayName')}</span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                      style={inputStyle}
                    />
                  </label>
                  <label>
                    <span style={labelStyle}>{t('fields.contactName')}</span>
                    <input
                      type="text"
                      value={form.contactName}
                      onChange={(event) => setForm((current) => ({ ...current, contactName: event.target.value }))}
                      style={inputStyle}
                    />
                  </label>
                  <label>
                    <span style={labelStyle}>{t('fields.email')}</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                      style={inputStyle}
                    />
                  </label>
                  <label>
                    <span style={labelStyle}>{t('fields.website')}</span>
                    <input
                      type="url"
                      value={form.websiteUrl}
                      onChange={(event) => setForm((current) => ({ ...current, websiteUrl: event.target.value }))}
                      style={inputStyle}
                    />
                  </label>
                  <label>
                    <span style={labelStyle}>{t('fields.linkedin')}</span>
                    <input
                      type="url"
                      value={form.linkedinUrl}
                      onChange={(event) => setForm((current) => ({ ...current, linkedinUrl: event.target.value }))}
                      style={inputStyle}
                    />
                  </label>
                </div>
              </div>
            </Card>

            <Card>
              <div style={{ display: 'grid', gap: 18 }}>
                <SectionHeader
                  eyebrow={t('settings.payoutEyebrow')}
                  title={t('settings.payoutTitle')}
                  subtitle={t('settings.payoutDescription')}
                />
                <label>
                  <span style={labelStyle}>{t('fields.payoutNotes')}</span>
                  <textarea
                    value={form.payoutNotes}
                    onChange={(event) => setForm((current) => ({ ...current, payoutNotes: event.target.value }))}
                    rows={5}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </label>
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', lineHeight: 1.6 }}>
                  {t('settings.payoutHint')}
                </div>

                <div>
                  <Button onClick={() => void handleSave()} disabled={saving}>
                    {saving ? t('settings.saving') : t('settings.save')}
                  </Button>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </Container>
  );
}
